import apiClient from './client';

// ---- Dashboards ----

export interface ExecutiveDashboardResponse {
  portfolios: {
    total: number;
    active: number;
  };
  projects: {
    total: number;
    onTrack: number;
    atRisk: number;
    offTrack: number;
  };
  financial: {
    totalBudget: number;
    totalSpent: number;
    totalPlanned: number;
    companyBudget?: null | {
      totalBudget: number;
      fiscalYear?: number | null;
      currency?: string | null;
      updatedAt?: string;
    };
    companyRemainingBudget?: number | null;
    companyUnallocatedBudget?: number | null;
    portfolioBreakdown?: Array<{
      id: string;
      name: string;
      status?: string | null;
      projects: number;
      budget: number;
      spent: number;
      remaining: number;
      utilization: number;
    }>;
    fiscalSeries?: Array<{
      fiscalYear: number;
      fiscalQuarter: number;
      period: string;
      funding: number;
      costs: number;
      expenses: number;
      revenue: number;
      totalCost: number;
      net: number;
    }>;
  };
  raid: {
    activeRisks: number;
    activeIssues: number;
  };
  requests: {
    pending: number;
  };
}

export const dashboardsApi = {
  getExecutive: () => apiClient.get<ExecutiveDashboardResponse>('/dashboards/executive'),
};

// ---- Projects (PPM / Prisma-backed) ----

export interface PpmProjectListItem {
  id: string;
  jiraKey?: string | null;
  name: string;
  description?: string | null;
  status?: string | null;
  priority?: string | null;
  portfolioId?: string | null;
  Portfolio?: { id: string; name: string } | null;
  budgetedCost?: number | null;
  actualCost?: number | null;
  leadUserId?: string | null;
  managerUserId?: string | null;
  startDate?: string | null;
  plannedEndDate?: string | null;
  tags?: string[];
  customFields?: any;
  createdAt: string;
  updatedAt: string;
}

export const ppmProjectsApi = {
  getAll: (params?: { portfolioId?: string; status?: string; priority?: string; source?: 'jira' | 'internal' }) =>
    apiClient.get<PpmProjectListItem[]>('/ppm/projects', { params }),
  getOne: (id: string) => apiClient.get<PpmProjectListItem>(`/ppm/projects/${id}`),
  create: (data: any) => apiClient.post<PpmProjectListItem>('/ppm/projects', data),
  update: (id: string, data: any) => apiClient.put<PpmProjectListItem>(`/ppm/projects/${id}`, data),
  remove: (id: string) => apiClient.delete(`/ppm/projects/${id}`),
  upsertJiraProject: (jiraKey: string, data: { name: string; portfolioId?: string | null }) =>
    apiClient.put<PpmProjectListItem>(`/ppm/projects/jira/${jiraKey}`, data),
  getSubprojects: (id: string) => apiClient.get<PpmProjectListItem[]>(`/ppm/projects/${id}/subprojects`),
  getMembers: (id: string) => apiClient.get<ProjectMember[]>(`/ppm/projects/${id}/members`),
  addMember: (id: string, data: { userId: string; role?: string; addedBy?: string | null }) =>
    apiClient.post<ProjectMember[]>(`/ppm/projects/${id}/members`, data),
  updateMember: (id: string, memberId: string, data: { role?: string }) =>
    apiClient.put<ProjectMember[]>(`/ppm/projects/${id}/members/${memberId}`, data),
  removeMember: (id: string, memberId: string) =>
    apiClient.delete<ProjectMember[]>(`/ppm/projects/${id}/members/${memberId}`),
  getMilestones: (id: string) => apiClient.get<any[]>(`/ppm/projects/${id}/milestones`),
  createMilestone: (id: string, data: { name: string; description?: string; status?: string; plannedDate: string; percentComplete?: number }) =>
    apiClient.post<any>(`/ppm/projects/${id}/milestones`, data),
  updateMilestone: (id: string, milestoneId: string, data: { name?: string; description?: string; status?: string; plannedDate?: string; actualDate?: string; percentComplete?: number }) =>
    apiClient.put<any>(`/ppm/projects/${id}/milestones/${milestoneId}`, data),
  removeMilestone: (id: string, milestoneId: string) => apiClient.delete<any>(`/ppm/projects/${id}/milestones/${milestoneId}`),
};

// ---- Discussions / Notes ----

export interface DiscussionPost {
  id: string;
  discussionId: string;
  userId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  User?: { id: string; email?: string | null; displayName?: string | null } | null;
}

export interface Discussion {
  id: string;
  title: string;
  projectId?: string | null;
  portfolioId?: string | null;
  requestId?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  creatorId?: string | null;
  isPinned?: boolean;
  isLocked?: boolean;
  createdAt: string;
  updatedAt: string;
  Posts?: DiscussionPost[];
}

export const discussionsApi = {
  getAll: (params?: { projectId?: string; portfolioId?: string; requestId?: string; creatorId?: string; entityType?: string; entityId?: string }) =>
    apiClient.get<Discussion[]>('/discussions', { params }),
  getEntity: (entityType: string, entityId: string) =>
    apiClient.get<Discussion[]>(`/discussions/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`),
  getOne: (id: string) => apiClient.get<Discussion>(`/discussions/${id}`),
  create: (data: any) => apiClient.post<Discussion>('/discussions', data),
  ensure: (data: { entityType: string; entityId: string; title?: string }) =>
    apiClient.post<Discussion>('/discussions/ensure', data),
  addPost: (discussionId: string, data: { content: string; parentId?: string | null }) =>
    apiClient.post<DiscussionPost>(`/discussions/${discussionId}/posts`, data),
  remove: (id: string) => apiClient.delete(`/discussions/${id}`),
};

// ---- Project Members / Permissions ----

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  role?: string | null;
  createdAt?: string;
  User?: { id: string; email?: string | null; displayName?: string | null; role?: string | null } | null;
}

// ---- Kanban Cards (project tasks) ----

export interface KanbanCard {
  id: string;
  boardId: string;
  title: string;
  description?: string | null;
  status: string;
  columnId?: string | null;
  assigneeId?: string | null;
  Assignee?: { id: string; email?: string | null; displayName?: string | null } | null;
  priority?: number | null;
  dueDate?: string | null;
  tags?: string[];
  position?: number | null;
  createdAt: string;
  updatedAt: string;
}

export const kanbanCardsApi = {
  getAll: (params?: { projectId?: string; boardId?: string }) =>
    apiClient.get<KanbanCard[]>('/kanban-cards', { params }),
  getBoard: (projectId: string) =>
    apiClient.get<Record<string, KanbanCard[]>>(`/kanban-cards/project/${projectId}/board`),
  create: (data: any) => apiClient.post<KanbanCard>('/kanban-cards', data),
  update: (id: string, data: any) => apiClient.put<KanbanCard>(`/kanban-cards/${id}`, data),
  remove: (id: string) => apiClient.delete(`/kanban-cards/${id}`),
  move: (id: string, column?: string, order?: number) => apiClient.put(`/kanban-cards/${id}/move`, { column, order }),
  claim: (id: string) => apiClient.put<KanbanCard>(`/kanban-cards/${id}/claim`, {}),
  unclaim: (id: string) => apiClient.put<KanbanCard>(`/kanban-cards/${id}/unclaim`, {}),
};

// ---- Financials (ProjectFinancial entries) ----

export type FinancialEntryType = 'FUNDING' | 'ALLOCATION' | 'COST' | 'EXPENSE' | 'REVENUE' | string;

export interface ProjectFinancialEntry {
  id: string;
  projectId: string;
  type: FinancialEntryType;
  amount: number;
  currency?: string | null;
  category?: string | null;
  description?: string | null;
  fiscalYear?: number | null;
  fiscalQuarter?: number | null;
  transactionDate: string;
  approvedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  Project?: { id: string; name: string } | null;
}

export const financialsApi = {
  getAll: (params?: { projectId?: string; type?: string; fiscalYear?: number; fiscalQuarter?: number }) =>
    apiClient.get<ProjectFinancialEntry[]>('/financials', { params }),
  create: (data: any) => apiClient.post<ProjectFinancialEntry>('/financials', data),
  update: (id: string, data: any) => apiClient.put<ProjectFinancialEntry>(`/financials/${id}`, data),
  remove: (id: string) => apiClient.delete(`/financials/${id}`),
  getCompanyBudget: () => apiClient.get<any>('/financials/company-budget'),
  upsertCompanyBudget: (data: any) => apiClient.put('/financials/company-budget', data),
};

// ---- Portfolios ----

export type PortfolioStatus = 'ACTIVE' | 'PLANNED' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED' | string;

export interface PortfolioListItem {
  id: string;
  name: string;
  description?: string | null;
  status: PortfolioStatus;
  ownerId?: string | null;
  strategicAlignment?: string | null;
  strategicAlignments?: string[]; // multi-select mapping (StrategicCategory names)
  strategicCategoryIds?: string[];
  totalBudget?: number | null;
  totalSpent?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
  Owner?: {
    id: string;
    email?: string | null;
    displayName?: string | null;
  } | null;
  Projects?: Array<{
    id: string;
    name: string;
    status: string;
    budgetedCost?: number | null;
    actualCost?: number | null;
    percentComplete?: number | null;
  }>;
  _count?: {
    Projects?: number;
    PortfolioMembers?: number;
  };
  membersCount?: number;
}

export interface PortfolioDetail extends PortfolioListItem {
  PortfolioMembers?: Array<{
    id: string;
    role?: string | null;
    User?: {
      id: string;
      email?: string | null;
      displayName?: string | null;
    };
  }>;
  Roadmaps?: Array<{
    id: string;
    name?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  }>;
  Discussions?: Array<any>;
}

export interface PortfolioMetricsResponse {
  portfolio: PortfolioDetail;
  metrics: {
    totalProjects: number;
    activeProjects: number;
    completedProjects: number;
    totalBudget: number;
    totalSpent: number;
    remainingBudget: number;
    budgetUtilization: number;
    avgCompletion: number;
    totalRisks: number;
    totalIssues: number;
  };
}

export interface StrategicAlignmentSlice {
  name: string;
  value: number;
}

export const portfoliosApi = {
  getAll: (params?: { status?: string; ownerId?: string; strategicAlignment?: string }) =>
    apiClient.get<PortfolioListItem[]>('/portfolios', { params }),
  getOne: (id: string) => apiClient.get<PortfolioDetail>(`/portfolios/${id}`),
  getMetrics: (id: string) => apiClient.get<PortfolioMetricsResponse>(`/portfolios/${id}/metrics`),
  getStrategicAlignment: () => apiClient.get<StrategicAlignmentSlice[]>('/portfolios/strategic-alignment'),
  getMembers: (id: string) => apiClient.get<any[]>(`/portfolios/${id}/members`),
  addMember: (id: string, data: { userId: string; role?: string | null }) => apiClient.post<any[]>(`/portfolios/${id}/members`, data),
  removeMember: (id: string, userId: string) => apiClient.delete<any[]>(`/portfolios/${id}/members/${userId}`),
  create: (data: any) => apiClient.post('/portfolios', data),
  update: (id: string, data: any) => apiClient.put(`/portfolios/${id}`, data),
  remove: (id: string) => apiClient.delete(`/portfolios/${id}`),
};

// ---- Requests / Intake ----

export type RequestStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'SUBMITTED_FOR_APPROVAL'
  | 'IN_EVALUATION'
  | 'APPROVED'
  | 'REJECTED'
  | string;

export type RequestPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'URGENT' | string;

export interface RequestListItem {
  id: string;
  title: string;
  description: string;
  requestType?: string | null;
  status: RequestStatus;
  priority?: RequestPriority | null;
  evaluationScore?: number | null;
  strategicAlignment?: number | null;
  strategicCategoryId?: string | null;
  StrategicCategory?: { id: string; name: string; color?: string | null } | null;
  complexity?: number | null;
  estimatedCost?: number | null;
  estimatedDuration?: number | null;
  estimatedValue?: number | null;
  roiImpact?: number | null;
  department?: string | null;
  businessCase?: string | null;
  rejectionReason?: string | null;
  submittedDate?: string | null;
  reviewedDate?: string | null;
  approvedDate?: string | null;
  projectId?: string | null;
  createdAt: string;
  updatedAt: string;
  Owner?: { id: string; displayName?: string | null; email?: string | null } | null;
  Creator?: { id: string; displayName?: string | null; email?: string | null } | null;
}

// ---- Strategic categories (Strategic Alignment themes) ----

export interface StrategicCategory {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface StrategicCategoryPortfolioSummary {
  totalCategories: number;
  totalPortfoliosMapped: number;
  totalProjectsMapped: number;
  totalDistinctPortfolios: number;
  totalDistinctProjects: number;
  totalDistinctPortfolioBudget: number;
  totalDistinctAllocatedBudget: number;
  rows: Array<{
    id: string;
    name: string;
    description?: string | null;
    color?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    portfoliosCount: number;
    projectsCount: number;
    portfolioBudget: number;
    allocatedBudget: number;
    portfolios: Array<{ id: string; name: string; totalBudget?: number | null }>;
    projects?: Array<{ id: string; name: string; status?: string | null; budgetedCost?: number | null }>;
  }>;
}

export const strategicCategoriesApi = {
  getAll: () => apiClient.get<StrategicCategory[]>('/strategic-categories'),
  portfolioSummary: () => apiClient.get<StrategicCategoryPortfolioSummary>('/strategic-categories/portfolio-summary'),
  create: (data: { name: string; description?: string | null; color?: string | null; startDate?: string | null; endDate?: string | null }) =>
    apiClient.post<StrategicCategory>('/strategic-categories', data),
  update: (id: string, data: { name?: string; description?: string | null; color?: string | null; startDate?: string | null; endDate?: string | null }) =>
    apiClient.put<StrategicCategory>(`/strategic-categories/${id}`, data),
  remove: (id: string) => apiClient.delete(`/strategic-categories/${id}`),
};

export const requestsApi = {
  getAll: (params?: {
    status?: string;
    priority?: string;
    ownerId?: string;
    creatorId?: string;
    department?: string;
    projectId?: string;
    requestType?: string;
    minScore?: number;
  }) => apiClient.get<RequestListItem[]>('/requests', { params }),
  getPending: (params?: { limit?: number }) =>
    apiClient.get<RequestListItem[]>('/requests/pending', { params }),
  create: (data: any) => apiClient.post('/requests', data),
  update: (id: string, data: any) => apiClient.put(`/requests/${id}`, data),
  remove: (id: string) => apiClient.delete(`/requests/${id}`),
  approve: (id: string, convertToProject?: boolean) =>
    apiClient.put(`/requests/${id}/approve`, { convertToProject: !!convertToProject }),
  reject: (id: string, reason?: string) => apiClient.put(`/requests/${id}/reject`, { reason }),
};

// ---- Resources ----

export interface ResourceAssignment {
  id: string;
  userId: string;
  projectId: string;
  role?: string | null;
  allocationPercentage?: number | null;
  hoursAllocated?: number | null;
  hoursActual?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: string | null;
  notes?: string | null;
  User?: { id: string; email?: string | null; displayName?: string | null; role?: string | null } | null;
  Project?: {
    id: string;
    name: string;
    status?: string | null;
    Portfolio?: { id: string; name: string } | null;
  } | null;
}

export interface CapacityPlan {
  id: string;
  userId: string;
  month: string;
  totalCapacity: number;
  allocated: number;
  available: number;
  approved: number;
  department?: string | null;
  metadata?: any;
}

export const resourceAssignmentsApi = {
  getAll: (params?: { userId?: string; projectId?: string; portfolioId?: string; role?: string; active?: boolean }) =>
    apiClient.get<ResourceAssignment[]>('/resource-assignments', { params }),
  create: (data: any) => apiClient.post<ResourceAssignment>('/resource-assignments', data),
  update: (id: string, data: any) => apiClient.put<ResourceAssignment>(`/resource-assignments/${id}`, data),
  remove: (id: string) => apiClient.delete(`/resource-assignments/${id}`),
};

export const capacityPlansApi = {
  getAll: (params?: { userId?: string; period?: string; department?: string }) =>
    apiClient.get<CapacityPlan[]>('/capacity-plans', { params }),
  getOverview: (period: string) => apiClient.get(`/capacity-plans/overview/${encodeURIComponent(period)}`),
  create: (data: any) => apiClient.post<CapacityPlan>('/capacity-plans', data),
  update: (id: string, data: any) => apiClient.put<CapacityPlan>(`/capacity-plans/${id}`, data),
  remove: (id: string) => apiClient.delete(`/capacity-plans/${id}`),
};

// ---- OKRs ----

export interface ObjectiveListItem {
  id: string;
  title: string;
  description?: string | null;
  status?: string | null;
  percentComplete?: number | null;
  ownerId?: string | null;
  portfolioId?: string | null;
  projectId?: string | null;
  quarter?: string | null;
  createdAt?: string;
  updatedAt?: string;
  Owner?: { id: string; displayName?: string | null; email?: string | null } | null;
  Project?: { id: string; name: string } | null;
  KeyResults?: Array<{ id: string; title?: string | null; percentComplete?: number | null }>;
}

export const objectivesApi = {
  getAll: (params?: { ownerId?: string; portfolioId?: string; projectId?: string; status?: string }) =>
    apiClient.get<ObjectiveListItem[]>('/objectives', { params }),
  getStats: (params?: { portfolioId?: string; projectId?: string }) =>
    apiClient.get<{ total: number; byStatus: Record<string, number>; averageProgress: number }>('/objectives/stats', { params }),
  create: (data: any) => apiClient.post<ObjectiveListItem>('/objectives', data),
  update: (id: string, data: any) => apiClient.put<ObjectiveListItem>(`/objectives/${id}`, data),
  remove: (id: string) => apiClient.delete(`/objectives/${id}`),
  updateProgress: (id: string) => apiClient.put<ObjectiveListItem>(`/objectives/${id}/progress`, {}),
};

export interface KeyResultListItem {
  id: string;
  objectiveId?: string | null;
  title: string;
  description?: string | null;
  targetValue: number;
  currentValue?: number | null;
  percentComplete?: number | null;
  createdAt?: string;
  updatedAt?: string;
  Objective?: { id: string; title: string } | null;
}

export const keyResultsApi = {
  getAll: (params?: { objectiveId?: string }) => apiClient.get<KeyResultListItem[]>('/key-results', { params }),
  getStats: (params?: { objectiveId?: string }) =>
    apiClient.get<{ total: number; averageProgress: number }>('/key-results/stats', { params }),
  create: (data: any) => apiClient.post<KeyResultListItem>('/key-results', data),
  update: (id: string, data: any) => apiClient.put<KeyResultListItem>(`/key-results/${id}`, data),
  remove: (id: string) => apiClient.delete(`/key-results/${id}`),
  updateProgress: (id: string, currentValue: number) => apiClient.put<KeyResultListItem>(`/key-results/${id}/progress`, { currentValue }),
};

// ---- Roadmaps ----

export interface RoadmapListItem {
  id: string;
  title?: string | null;
  name?: string | null;
  status?: string | null;
  portfolioId?: string | null;
  projectId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const roadmapsApi = {
  getAll: (params?: { portfolioId?: string }) => apiClient.get<RoadmapListItem[]>('/roadmaps', { params }),
  getUpcomingMilestones: (params?: { portfolioId?: string; days?: number }) =>
    apiClient.get<any[]>('/roadmaps/milestones/upcoming', { params }),
  getTimeline: (id: string) => apiClient.get<any>(`/roadmaps/${id}/timeline`),
  create: (data: any) => apiClient.post<RoadmapListItem>('/roadmaps', data),
  update: (id: string, data: any) => apiClient.put<RoadmapListItem>(`/roadmaps/${id}`, data),
  remove: (id: string) => apiClient.delete(`/roadmaps/${id}`),
};

export interface RoadmapItemListItem {
  id: string;
  roadmapId: string;
  projectId?: string | null;
  title: string;
  description?: string | null;
  startDate?: string;
  endDate?: string;
  color?: string | null;
  lane?: string | null;
  status?: string | null;
  createdAt?: string;
  updatedAt?: string;
  Roadmap?: { id: string; name: string } | null;
  Project?: { id: string; name: string; status?: string | null } | null;
}

export const roadmapItemsApi = {
  create: (roadmapId: string, data: any) => apiClient.post<RoadmapItemListItem>(`/roadmaps/${roadmapId}/items`, data),
  update: (id: string, data: any) => apiClient.put<RoadmapItemListItem>(`/roadmaps/items/${id}`, data),
  remove: (id: string) => apiClient.delete(`/roadmaps/items/${id}`),
};

// ---- Templates ----

export interface ProjectTemplate {
  id: string;
  name: string;
  description?: string | null;
  type?: string;
  category?: string | null;
  includeSubprojects?: boolean;
  includeNotes?: boolean;
  includePermissions?: boolean;
  includeMilestones?: boolean;
  includeResources?: boolean;
  includeTasks?: boolean;
  includeResourcePlanning?: boolean;
  includeFiles?: boolean;
  includeStaffingRequests?: boolean;
  templateData?: any;
  isActive?: boolean;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const templatesApi = {
  getOne: (id: string) => apiClient.get<ProjectTemplate>(`/templates/${id}`),
};


